import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LeafletDirective, LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoCurrencyPipe, TranslocoDatePipe, TranslocoDecimalPipe, TranslocoLocaleService, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { control, divIcon, latLng, LeafletMouseEvent, Map, marker, Marker, MarkerClusterGroup, MarkerOptions, tileLayer } from 'leaflet';
import { MenuItem } from 'primeng/api';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { Tag, TagModule } from 'primeng/tag';
import { ToggleSwitchChangeEvent, ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, debounceTime, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { Bike, BikeFilter, BikeStatus, getDefaultBike } from '../../interfaces/bike';
import { BikeModel } from '../../interfaces/bikeModel';
import { getDefaultStation, Station, StationFilter, StationOpeningTime } from '../../interfaces/station';
import { Column } from '../../interfaces/table';
import { DurationPipe } from '../../pipes/duration.pipe';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { WebsocketService } from '../../services/websocket.service';
import { getPathKey } from '../../utilities/util';
import { AuthStateService } from '../../services/authstate.service';
import { BatteryDisplayComponent } from '../battery-display-component/battery-display-component';
import { LongPressDirective } from '../../directives/long-press.directive';

interface StationMarkerOptions extends MarkerOptions {
  station: Station;
}

interface BikeMarkerOptions extends MarkerOptions {
  bike: Bike;
}

@Component({
  selector: 'app-admin-map-view',
  standalone: true,
  imports: [
    CommonModule,
    LeafletModule,
    LeafletMarkerClusterModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    TranslocoDirective,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    AutoComplete,
    MenuModule,
    MultiSelectModule,
    SelectModule,
    TagModule,
    SkeletonModule,
    TooltipModule,
    ToggleSwitchModule,
    InputMaskModule,
    TranslocoDatePipe,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe, DurationPipe],
  templateUrl: './admin-map-view.component.html',
  styleUrl: './admin-map-view.component.scss'
})
export class AdminMapViewComponent implements OnDestroy, AfterViewInit {

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- INIZIALIZZAZIONI ----------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? MAPPA
  @ViewChild(LeafletDirective) leaflet!: LeafletDirective;
  userLat = Number(localStorage.getItem("defaultLat") ?? 40.955854);
  userLong = Number(localStorage.getItem("defaultLng") ?? 17.288318);
  mapLayers: any = [];
  stationMarkers: any = [];
  bikesMarkers: any = [];
  private map?: Map;

  mapOptions = {
    layers: [
      tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png', { maxZoom: 20, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' })
    ],
    zoom: 14,
    center: latLng(this.userLat, this.userLong),
    zoomControl: false,
  };

  mapLocationPickerPin: any;
  mapLocationPickerMode: 'bike' | 'station' = 'bike';
  mapLocationPickerActive: boolean = false;
  mapLocationPickerSelection?: [number, number];
  get currentLocationPickerSelection() {
    if (this.mapLocationPickerSelection === undefined) return "-- / --"
    return this.mapLocationPickerSelection[0].toFixed(3) + ' / ' + this.mapLocationPickerSelection[1].toFixed(3)
  }

  stationsClusterGroup = new MarkerClusterGroup();
  bikesClusterGroup = new MarkerClusterGroup({
    disableClusteringAtZoom: 14,
    iconCreateFunction: (cluster) => {
      return divIcon({
        html: `<div class="bike-cluster-marker">${cluster.getChildCount()}</div>`,
        className: 'bike-cluster',
        iconSize: [30, 30]
      });
    },
    spiderfyOnMaxZoom: false
  });

  private bikeMarkersMap: globalThis.Map<string, Marker> = new globalThis.Map();
  private stationMarkersMap: globalThis.Map<string, Marker> = new globalThis.Map();
  private bikesVisible = true;
  private bikesReduced = false;
  private stationsVisible = true;
  private stationsReduced = false;

  //? TABS
  activeTab: string = '';


  //? DATI E TABELLE
  bikesColumns: Column[] = [
    { field: 'bikeId', header: 'id', visible: false },
    { field: 'code', header: 'code', visible: true },
    { field: 'progressive', header: 'progressive', visible: true },
    { field: 'description', header: 'description', visible: false },
    { field: 'model', header: 'model', visible: true, nestedField: 'modelDetails.code' },
    { field: 'currentStation', header: 'currentStation', visible: true, nestedField: 'currentStationDetails.code' },
    {
      field: 'status', header: 'status', visible: true, nestedField: 'statusDetails.description', pipe: "translate", wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "{{statusDetails.icon}}",
        styleClass: "table-tag opacity",
        style: { "--bg-color": "{{statusDetails.color}}" },
      },
    },
    {
      field: 'iotStatus', header: 'iot', visible: true, pipe: "translate", wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "hourglass",
        styleClass: "table-tag default",
        severity_if_iotStatus_ok: "success",
        severity_if_iotStatus_na: "warn",
        severity_if_iotStatus_ni: "secondary",
        icon_if_iotStatus_ok: "pi pi-cloud",
        icon_if_iotStatus_na: "pi pi-hourglass",
        icon_if_iotStatus_ni: "pi pi-times",
      }
    },
    { field: 'imei', header: 'imei', visible: false, filterable: true },
    {
      field: 'iotBattery', header: 'iotBattery', nestedField: "iotDetails.lock.battery.level", wrapper: BatteryDisplayComponent, wrapperConfig: { value: "{{value}}" }
    },
  ]
  bikes: Bike[] = []
  iotConnectedImeis: string[] = [];
  iotConnectedImeisAutocomplete: string[] = [];

  get bikesFilterFields() {
    return this.bikesColumns.filter(column => column.visible || column.filterable).map(column => column.field);
  }

  stationColumns: Column[] = [
    { field: 'stationId', header: 'id', visible: false },
    { field: 'code', header: 'code', visible: true },
    { field: 'description', header: 'description', visible: true },
    { field: 'lat', header: 'latitute', visible: false },
    { field: 'lng', header: 'longitude', visible: false },
    { field: 'address', header: 'address', visible: true },
    { field: 'city', header: 'city', visible: true },
    { field: 'zip', header: 'zip', visible: false },
    { field: 'countryCode', header: 'country', visible: false },
  ]
  stations: Station[] = []
  get stationsFilterFields() {
    return this.stationColumns.filter(column => column.visible).map(column => column.field);
  }

  selectedBikes: any[] = []
  selectedStations: Station[] = []

  bikesLoading$ = new BehaviorSubject<boolean>(false)
  bikesLoadingUI$ = this.bikesLoading$.pipe(
    debounceTime(200)
  );

  stationsLoading$ = new BehaviorSubject<boolean>(false)
  stationsLoadingUI$ = this.stationsLoading$.pipe(
    debounceTime(200)
  );

  _bikesTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.bikeId
  };

  _stationsTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.stationId
  };

  //? VIEW
  dataSectionOpen: boolean = true;

  stationDialogVisible: boolean = false;
  stationFiltersDialogVisible: boolean = false;
  stationDialogData: Station = getDefaultStation();
  stationFilters: StationFilter = {
    options: {
      city: [],
      zip: [],
      countryCode: [],
      province: []
    },
    cache: {
      city: [],
      zip: [],
      countryCode: [],
      province: [],
    },
    values: {
    },
    applied: {}
  };
  get appliedStationFiltersCount() {
    let cnt = Object.values(this.stationFilters.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }
  get isNewStationDialog() {
    return this.stationDialogData.stationId === ''
  }

  bikeDialogVisible: boolean = false;
  bikeFiltersDialogVisible: boolean = false;
  bikeDialogData: Bike = getDefaultBike();
  bikeFilters: BikeFilter = {
    options: {
      status: [],
      currentStation: [],
      model: []
    },
    cache: {
      status: [],
      currentStation: [],
      model: []
    },
    values: {},
    applied: {}
  };
  get appliedBikeFiltersCount() {
    let cnt = Object.values(this.bikeFilters.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }

  get isNewBikeDialog() {
    return this.bikeDialogData.bikeId === ''
  }

  autocompleteSearchAddressQuery: string = '';
  addressAutocompleteLoading: boolean = false;
  addressAutocompleteResults: any[] = [];

  _stationBatchMenuStructure: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteStations()
          }
        }
      ]
    }
  ]
  _bikeBatchMenuStructure: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteBikes()
          }
        }
      ]
    }
  ]

  stationBatchMenu: MenuItem[] = []
  bikeBatchMenu: MenuItem[] = []

  _bikesStatuses: BikeStatus[] = []
  bikesStatuses: BikeStatus[] = []

  bikesModels: BikeModel[] = []

  //? ALTRO
  destroy$ = new Subject<void>();
  fileState: {
    logoPicturePreview?: string;
    logoPictureFile?: File;
    stationPicturePreview?: string;
    stationPictureFile?: File;
    outsidePicturePreview?: string;
    outsidePictureFile?: File;
  } = {};
  deletedPictures: string[] = [];

  activeStationEditSection: "data" | "details" | "times" = "data";
  activeBikeEditSection: "data" | "iot" = "data";
  stationOpeningTimes: { enabled: boolean, times: (StationOpeningTime[] | null | undefined) }[] = [];
  copyingTimesIndex?: number;
  wsPositionsSub?: Subscription
  connImeisSub?: Subscription
  bikeDetailsSub?: Subscription

  iotKeysMapping: { [key: string]: string | null } = {
    "latitude": "lat",
    "longitude": "lng",
    "altitude": "locationAltitude",
    "accuracy": "locationAccuracy",
    "satellites": "locationSatellites",
    "lock_power_percentage": "iotBatteryPercentage",
    "lock_current_voltage": "iotBatteryVoltage",
    "network_signal_value": "iotNetworkSignal",
    "lock_status": "iotIsLocked",
    // "iotLockChangeTimestamp" has a null value, so it doesn't get reversed
    "user_id": "currentUser",
    "bike_power_percentage": "bikeBatteryPercentage",
    "power_status": "bikeIsOn",
    "speed_mode": "bikeSpeedMode",
    "start_mode": "bikeStartMode",
    "low_speed_limit": "bileLowSpeedLimit",
    "medium_speed_limit": "bikeMediumSpeedLimit",
    "high_speed_limit": "bikeHighSpeedLimit",
    "throttle_response": "bikeThrottleResponse",
    "lock_motor": "bikeLockMotor",
    "headlight_switch": "bikeHeadlight",
    "current_speed": "bikeCurrentSpeed",
    "charging_status": "bikeCharging",
    "fault_code": "bikeFaultCode",
    "error_code": "iotErrorCode"
  };


  stationTimesSwitchChange(e: ToggleSwitchChangeEvent, dayIndex: number) {
    let curDayData = this.stationOpeningTimes[dayIndex]
    if (e.checked) {
      if ((curDayData.times ?? []).length == 0) {
        this.stationOpeningTimes[dayIndex].times = [{ "open": "00:00", "close": "23:59" }]
      }
    }
  }

  addTime(dayIndex: number) {
    let curDayData = this.stationOpeningTimes[dayIndex]
    if (curDayData.times) {
      curDayData.times.push({ "open": "", "close": "" })
    }
  }

  deleteTime(dayIndex: number, timeIndex: number) {
    let curDayData = this.stationOpeningTimes[dayIndex]
    if (curDayData.times) {
      curDayData.times.splice(timeIndex, 1)
      if (curDayData.times.length == 0) {
        curDayData.times = undefined
        this.stationOpeningTimes[dayIndex].enabled = false
      }
    }
  }

  copyTimes(dayIndex: number) {
    this.copyingTimesIndex = dayIndex
  }

  cancelCopyTimes() {
    this.copyingTimesIndex = undefined
  }

  pasteTimes(dayIndex: number) {
    if (this.copyingTimesIndex !== undefined) {
      this.stationOpeningTimes[dayIndex] = structuredClone(this.stationOpeningTimes[this.copyingTimesIndex])
    }
  }

  get isCopying() {
    return this.copyingTimesIndex !== undefined
  }


  formatTime(time: StationOpeningTime, field: 'open' | 'close', dayIndex: number, timeIndex: number) {
    if (time) {
      let completed: string | undefined = undefined
      if (time[field].endsWith(":--")) {
        completed = time[field].replace(":--", ":00")
      } else if (time[field].length === 2) {
        completed = time[field] + ":00"
      }
      if (completed) {
        setTimeout(() => {
          this.stationOpeningTimes[dayIndex].times![timeIndex][field] = completed
        }, 1);
      }
    }
  }

  validateOpeningTimes() {
    for (let dayIndex = 0; dayIndex < this.stationOpeningTimes.length; dayIndex++) {
      const dayData = this.stationOpeningTimes[dayIndex];

      if (!dayData.enabled || !dayData.times) continue;

      // Check if all times are valid
      const invalidTimes = dayData.times.some(time =>
        time.open === undefined || time.close === undefined ||
        time.open.length < 5 ||
        time.close.length < 5

      );

      if (invalidTimes) {
        this.notifications.showNotification({
          severity: "warn",
          title: "errors.invalidData",
          detail: "invalidTimeFormat",
          detailParams: {
            day: this.transloco.translate("primeng.dayNames")[dayIndex]
          }
        })
        return false
      }

      // Check for invalid time ranges (open >= close)
      const invalidRange = dayData.times.some(time => time.open >= time.close);
      if (invalidRange) {
        this.notifications.showNotification({
          severity: "warn",
          title: "errors.invalidData",
          detail: "invalidTimeRange",
          detailParams: {
            day: this.transloco.translate("primeng.dayNames")[dayIndex]
          }
        })
        return false
      }

      // Check for overlapping ranges
      const sortedTimes = [...dayData.times].sort((a, b) => a.open.localeCompare(b.open));
      for (let i = 0; i < sortedTimes.length - 1; i++) {
        if (sortedTimes[i].close > sortedTimes[i + 1].open) {
          this.notifications.showNotification({
            severity: "warn",
            title: "errors.invalidData",
            detail: "overlappingTimes",
            detailParams: {
              day: this.transloco.translate("primeng.dayNames")[dayIndex]
            }
          })
          return false
        }
      }
    }
    return true;
  }

  get curAuthLevel() {
    return this.auth.loginData.user.authLevel
  }

  get isStationManager() {
    return this.curAuthLevel == "4"
  }

  get canCreateBike() {
    return !this.isStationManager
  }

  get canEditBike() {
    return !this.isStationManager
  }

  get canDeleteBike() {
    return !this.isStationManager
  }

  get canCreateStation() {
    return !this.isStationManager
  }

  get canEditStation() {
    return true
  }

  get canDeleteStation() {
    return !this.isStationManager
  }


  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- LIFE CYCLE - EVENTI -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private confirmDialogs: ConfirmDialogsService,
    private notifications: NotificationsService,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private translocoCurrency: TranslocoCurrencyPipe,
    private translocoDecimal: TranslocoDecimalPipe,
    private translocoPercent: TranslocoPercentPipe,
    private durationPipe: DurationPipe,
    private route: ActivatedRoute,
    private router: Router,
    private localeService: TranslocoLocaleService,
    private ws: WebsocketService,
    private auth: AuthStateService,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    // if (navigator.geolocation) {
    //   const options = {
    //     enableHighAccuracy: true,
    //     timeout: 5000,
    //     maximumAge: 0,
    //   };

    //   // Using getCurrentPosition instead of watchPosition
    //   navigator.geolocation.getCurrentPosition(
    //     (position) => {
    //       this.zone.run(() => {
    //         const longitude = position.coords.longitude;
    //         const latitude = position.coords.latitude;
    //         this.userLat = latitude;
    //         this.userLong = longitude;
    //         console.log(this.userLat, this.userLong);

    //         this.currentPosition = circleMarker(latLng(latitude, longitude), this.circleStyle);

    //         if (this.map && !this.map.getBounds().contains([latitude, longitude])) {
    //           this.setViewOnUser()
    //         }
    //       });
    //     },
    //     null,
    //     options
    //   );
    // }

    transloco.events$.pipe(
      takeUntil(this.destroy$),
      filter((event) => event.type === "translationLoadSuccess")
    ).subscribe((e) => {
      this.stationBatchMenu = this.translateObjectValues(this._stationBatchMenuStructure, 'label')
      this.bikeBatchMenu = this.translateObjectValues(this._bikeBatchMenuStructure, 'label')
      this.bikesStatuses = this.translateObjectValues(this._bikesStatuses, 'description')
    });

    this.route.params.subscribe(params => {
      const tab = params['tab'];
      this.setActiveTab(tab, false);
    });
  }

  ngOnDestroy() {
    this.mapLayers.forEach((layer: any) => {
      layer.remove();
    });
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.transloco.langChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.stationBatchMenu = this.translateObjectValues(this._stationBatchMenuStructure, 'label')
      this.bikeBatchMenu = this.translateObjectValues(this._bikeBatchMenuStructure, 'label')
      this.bikesStatuses = this.translateObjectValues(this._bikesStatuses, 'description')
    });

    this.ws.getConnectionStatus().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (status) => {
        if (status === "logged") {
          this.wsPositionsSub?.unsubscribe()
          this.wsPositionsSub = this.ws.subscribe("POSITION_TRACKING").pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            next: (rawData) => {
              if (!rawData || Object.keys(rawData).length == 0) return
              let data = rawData.data
              let imei = rawData.imei
              let bikeIndex = this.bikes.findIndex(b => b.imei == imei)
              if (bikeIndex != -1) {
                let bike = this.bikes[bikeIndex]
                let locationDetails: any = {
                  lat: data.latitude,
                  lng: data.longitude,
                  alt: data.altitude,
                  accuracy: data.accuracy,
                  numberOfSatellites: data.satellites,
                  lastUpdate: Date.now(),
                }

                if (!bike.iotDetails) {
                  bike.iotDetails = {}
                }

                if (!bike.iotDetails.location) {
                  bike.iotDetails.location = {}
                }

                bike.iotDetails.location = locationDetails

                this.bikes[bikeIndex] = bike
                let mrk = this.bikeMarkersMap.get(bike.bikeId)
                if (mrk) {
                  this.animateMarkerMove(mrk, latLng(locationDetails.lat, locationDetails.lng))
                }
              }
            }
          })

          this.connImeisSub = this.ws.subscribe("CONNECTED_IMEIS").pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            next: (data) => {
              if (!data || !Array.isArray(data)) return
              this.iotConnectedImeis = data
              this.bikes.forEach((bike) => {
                bike.iotStatus = !bike.imei ? "ni" : (bike.iotDetails && this.iotConnectedImeis.includes(bike.imei) ? "ok" : "na")
              })
            }
          })
        } else {
          this.wsPositionsSub?.unsubscribe()
          this.connImeisSub?.unsubscribe()
        }
      }
    })
  }

  //? MAPPA
  onMapReady(map: Map) {
    this.map = map;

    this.map.addLayer(this.stationsClusterGroup);
    this.map.addLayer(this.bikesClusterGroup);

    control.zoom({
      position: 'bottomright',
      zoomInText: '+',
      zoomOutText: '-'
    }).addTo(map);

    this.refreshStations();
    this.refreshBikes()

    setTimeout(() => {
      this.map?.invalidateSize()
    }, 1);
  }

  onMapClick(event: LeafletMouseEvent) {
    const { lat, lng } = event.latlng;

    if (this.mapLocationPickerActive) {
      this.mapLocationPickerSelection = [lat, lng];
      if (this.mapLocationPickerPin) {
        this.mapLocationPickerPin.remove();
      }
      this.mapLocationPickerPin = marker([lat, lng], {
        icon: divIcon({
          className: this.mapLocationPickerMode + '-marker-icon picker',
          html: '<div class="marker-circle"><span class="material-symbols-outlined ' + this.mapLocationPickerMode + '-icon">edit_location</span></div>',
          iconSize: [40, 40]
        })
      });
      this.mapLocationPickerPin.addTo(this.map!);

    }
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- ALTRO ---------------------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? MAPPA
  setViewOnUser() {
    this.map?.flyTo(latLng(this.userLat, this.userLong), 14, { duration: 1 });
  }

  panToPoint(lat: number, lng: number, zoom?: number) {
    this.map?.flyTo(latLng(lat, lng), zoom, { duration: 1 });
  }

  refreshBikesMarkers() {
    this.bikeMarkersMap.clear()
    this.bikesClusterGroup.clearLayers();
    this.bikesMarkers.forEach((marker: any) => {
      marker.remove();
    });
    this.bikesMarkers = [];

    if (!this.bikesVisible) return;

    this.bikes.forEach(bike => {
      if (bike.iotDetails?.location?.lat && bike.iotDetails?.location?.lng) {
        const markerElement = marker(
          latLng(bike.iotDetails.location.lat, bike.iotDetails.location.lng),
          {
            bike: bike,
            icon: divIcon({
              className: 'bike-marker-icon' + (this.bikesReduced ? ' reduced' : ''),
              // html: '<div class="marker-circle" style="--status-color: ' + bike.statusDetails?.color + ';"><span class="material-symbols-outlined bike-icon">pedal_bike</span><span class="bike-label">' + bike.code + '</span></div>',
              html: '<div class="marker-circle" style="--status-color: ' + bike.statusDetails?.color + ';"></div>',
              // iconSize: [40, 40],
              iconSize: [20, 20],
              // iconAnchor: [25, 25]
            })
          } as BikeMarkerOptions
        );

        const popupContent = `
          <div>
            <strong>${bike.code}</strong><br>
            ${this.transloco.translate(bike.statusDetails?.description ?? '%t%--')}
          </div>
        `;

        // Add popup behavior
        markerElement.bindPopup(popupContent, {
          closeButton: false,
          className: 'bike-popup',
          offset: [0, -3]
        });

        if (this.bikesReduced) {
          markerElement.setOpacity(0.3);
        } else {


          markerElement.on('mouseover', (e) => {
            markerElement.openPopup();
          });


          markerElement.on('mouseout', (e) => {
            setTimeout(() => {
              markerElement.closePopup();
            }, 200);
          });

          markerElement.on('click', (e) => {
            this.zone.run(() => {
              this.openBikeDialog(bike);
            });
          })
        }

        this.bikesClusterGroup.addLayer(markerElement);
        this.bikesMarkers.push(markerElement);
        this.bikeMarkersMap.set(bike.bikeId, markerElement);
      }
    });
  }

  refreshStationsMarkers() {
    this.stationMarkersMap.clear()
    this.stationsClusterGroup.clearLayers();
    this.stationMarkers.forEach((marker: any) => {
      marker.remove();
    });
    this.stationMarkers = [];

    if (!this.stationsVisible) return;

    this.stations.forEach(station => {
      const markerElement = marker(
        latLng(station.lat, station.lng),
        {
          station: station,
          icon: divIcon({
            className: 'station-marker-icon' + (this.stationsReduced ? ' reduced' : ''),
            html: '<div class="marker-circle"><span class="material-symbols-outlined station-icon">bike_dock</span><span class="station-label">' + station.code + '</span></div>',
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          })
        } as StationMarkerOptions
      );

      const popupContent = `
      <div>
        <strong>${station.description}</strong><br>
        Code: ${station.code}
      </div>
    `;

      // Add popup behavior
      markerElement.bindPopup(popupContent, {
        closeButton: false,
        className: 'station-popup',
        offset: [0, -3]
      });

      if (this.stationsReduced) {
        markerElement.setOpacity(0.3);
      } else {

        markerElement.on('mouseover', (e) => {
          markerElement.openPopup();
        });


        markerElement.on('mouseout', (e) => {
          setTimeout(() => {
            markerElement.closePopup();
          }, 200);
        });

        markerElement.on('click', (e) => {
          this.zone.run(() => {
            this.openStationDialog(station);
          });
        })
      }

      this.stationsClusterGroup.addLayer(markerElement);
      this.stationMarkers.push(markerElement);
      this.stationMarkersMap.set(station.stationId, markerElement);
    });
  }

  private updateMarkersState() {
    if (this.stationsVisible) {
      if (!this.stationsClusterGroup.hasLayer(this.stationMarkers[0])) {
        this.stationsClusterGroup.addLayers(this.stationMarkers);
      }
      this.stationsClusterGroup.eachLayer((layer: any) => {
        if (layer instanceof Marker) {
          if (this.stationsReduced) {
            layer.setOpacity(0.3);
            layer.closePopup();
            layer.off('click mouseover mouseout');
            if (layer.getElement()) {
              layer.getElement()!.style.cursor = 'default';
              layer.getElement()!.style.pointerEvents = 'none';
            }
          } else {
            layer.setOpacity(1);
            layer.on('click', (e) => {
              this.zone.run(() => {
                this.openStationDialog((layer.options as StationMarkerOptions).station);
              });
            });
            layer.on('mouseover', () => layer.openPopup());
            layer.on('mouseout', () => setTimeout(() => layer.closePopup(), 200));
            if (layer.getElement()) {
              layer.getElement()!.style.cursor = 'pointer';
              layer.getElement()!.style.pointerEvents = 'auto';
            }
          }
        }
      });
    } else {
      this.stationsClusterGroup.clearLayers();
    }

    if (this.bikesVisible) {
      if (!this.bikesClusterGroup.hasLayer(this.bikesMarkers[0])) {
        this.bikesClusterGroup.addLayers(this.bikesMarkers);
      }
      this.bikesClusterGroup.eachLayer((layer: any) => {
        if (layer instanceof Marker) {
          if (this.bikesReduced) {
            layer.setOpacity(0.3);
            layer.closePopup();
            layer.off('click mouseover mouseout');
            if (layer.getElement()) {
              layer.getElement()!.style.cursor = 'default';
              layer.getElement()!.style.pointerEvents = 'none';
            }
          } else {
            layer.setOpacity(1);
            layer.on('click', (e) => {
              this.zone.run(() => {
                this.openBikeDialog((layer.options as BikeMarkerOptions).bike);
              });
            });
            layer.on('mouseover', () => layer.openPopup());
            layer.on('mouseout', () => setTimeout(() => layer.closePopup(), 200));
            if (layer.getElement()) {
              layer.getElement()!.style.cursor = 'pointer';
              layer.getElement()!.style.pointerEvents = 'auto';
            }
          }
        }
      });
    } else {
      this.bikesClusterGroup.clearLayers();
    }
  }

  private animateMarkerMove(marker: Marker, newLatLng: L.LatLng, duration: number = 1000) {
    const startLatLng = marker.getLatLng();
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const lat = startLatLng.lat + (newLatLng.lat - startLatLng.lat) * progress;
      const lng = startLatLng.lng + (newLatLng.lng - startLatLng.lng) * progress;

      marker.setLatLng([lat, lng]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  //? TABS
  setActiveTab(tab: string, fromUserAction: boolean = true) {
    if (tab != this.activeTab) {
      this.hideStationFilters();
      this.hideBikeFilters();

      // Only navigate if change was triggered by user action
      if (fromUserAction) {
        this.router.navigate(['/admin', 'mapview', tab]);
      }
    }

    this.activeTab = tab;
    this.stationsReduced = (tab === "bikes");
    this.bikesReduced = (tab === "stations");
    this.updateMarkersState();
  }


  //? DATI E TABELLE
  refreshBikes() {
    this.bikesLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "getBikes", {
      filters: this.bikeFilters.values
    }).subscribe({
      next: (resp: any) => {
        this.bikeFilters.applied = structuredClone(this.bikeFilters.values)
        this.bikes = resp.data;
        this.selectedBikes = []
        this.refreshBikesMarkers()
        this.bikesLoading$.next(false);
        this.http.post(this.ENV.apiUrl + "getBikesFilters", {}).subscribe({
          next: (resp: any) => {
            this.bikeFilters.options = resp.data;
          }
        })
      },
      error: () => {
        this.bikesLoading$.next(false);
      }
    })

    this.http.post(this.ENV.apiUrl + "getBikesStatuses", {}).subscribe({
      next: (resp: any) => {
        this._bikesStatuses = resp.data;
        this.bikesStatuses = this.translateObjectValues(this._bikesStatuses, 'description')
      }
    })

    this.http.post(this.ENV.apiUrl + "getBikeModels", {}).subscribe({
      next: (resp: any) => {
        this.bikesModels = resp.data;
      }
    })
  }

  refreshStations() {
    this.stationsLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "getStations", {
      filters: this.stationFilters.values
    }).subscribe({
      next: (resp: any) => {
        this.stationFilters.applied = structuredClone(this.stationFilters.values)
        this.stations = resp.data;
        this.selectedStations = []
        this.refreshStationsMarkers()
        this.stationsLoading$.next(false);
        this.http.post(this.ENV.apiUrl + "getStationsFilters", {}).subscribe({
          next: (resp: any) => {
            this.stationFilters.options = resp.data;
          }
        })
      },
      error: () => {
        this.stationsLoading$.next(false);
      }
    })
  }

  onBikeSelectionChange(sel: any) {
    if (sel.length == 1) {
      if (sel[0].lat == 0 && sel[0].lng == 0) {
        this.setViewOnUser()
      } else {
        // TODO MODIFICARE PER GESTIRE QUANDO IN STAZIONE
        console.log("MODIFICARE");
        if (sel[0].iotDetails?.location) {
          this.panToPoint(sel[0].iotDetails.location.lat, sel[0].iotDetails.location.lng, 18)
        }
      }
    } else {
      this.setViewOnUser()
    }
  }

  onStationSelectionChange(sel: any) {
    if (sel.length == 1) {
      this.panToPoint(sel[0].lat, sel[0].lng, 16)
    } else {
      this.setViewOnUser()
    }
  }

  openBikeFilters() {
    this.bikeFiltersDialogVisible = true;
  }

  toggleBikeFilters() {
    this.bikeFiltersDialogVisible = !this.bikeFiltersDialogVisible;
  }

  hideBikeFilters() {
    this.bikeFiltersDialogVisible = false;
  }


  openStationFilters() {
    this.stationFiltersDialogVisible = true;
  }

  toggleStationFilters() {
    this.stationFiltersDialogVisible = !this.stationFiltersDialogVisible;
  }

  hideStationFilters() {
    this.stationFiltersDialogVisible = false;
  }

  //? VIEW
  toggleDataSection(evt: Event) {
    evt.stopPropagation()
    this.dataSectionOpen = !this.dataSectionOpen;
    setTimeout(() => {
      this.map?.invalidateSize()
    }, 200);
  }

  openStationDialog(station?: Station) {
    if (station) {
      this.stationsLoading$.next(true)
      this.http.post(this.ENV.apiUrl + "getStation", { ...station, excludeRefs: "*" }).subscribe({
        next: (resp: any) => {
          this.selectedStations = []
          this.stationDialogData = resp.data

          // Initialize fileState with existing image paths
          this.fileState = {
            logoPicturePreview: resp.data.logoPicture ? "assets/stations/" + resp.data.logoPicture : undefined,
            stationPicturePreview: resp.data.stationPicture ? "assets/stations/" + resp.data.stationPicture : undefined,
            outsidePicturePreview: resp.data.outsidePicture ? "assets/stations/" + resp.data.outsidePicture : undefined,
          };

          delete this.stationDialogData.logoPicture
          delete this.stationDialogData.stationPicture
          delete this.stationDialogData.outsidePicture

          // Reset deletedPictures
          this.deletedPictures = [];

          for (let i = 0; i < 7; i++) {
            if (i in resp.data.openingTimes) {
              this.stationOpeningTimes.push({ enabled: true, times: resp.data.openingTimes[i] })
            } else {
              this.stationOpeningTimes.push({ enabled: false, times: [] })
            }
          }

          this.stationDialogVisible = true;
          this.stationsLoading$.next(false);
        },
        error: () => {
          this.stationsLoading$.next(false);
        }
      })
    } else {
      if (!this.canCreateStation) return
      this.stationDialogData = getDefaultStation()
      this.stationDialogVisible = true
    }
  }

  hideStationDialog(form?: NgForm) {
    this.stationDialogVisible = false;
    this.onHideStationDialog(form)
  }

  onHideStationDialog(form?: NgForm) {
    if (this.mapLocationPickerActive) return;
    this.stationDialogData = getDefaultStation();
    this.fileState = {};
    this.deletedPictures = [];
    this.stationOpeningTimes = [];
    this.copyingTimesIndex = undefined;
    if (form) {
      form.resetForm(this.stationDialogData)
    }
    const fileInputs = document.querySelectorAll('.hidden-input');
    fileInputs.forEach((input: any) => {
      input.value = ''; // Reset file inputs
    });
    this.setViewOnUser()
  }

  saveStation() {
    this.cancelCopyTimes()
    if (!this.validateOpeningTimes()) {
      return;
    }

    this.stationsLoading$.next(true);

    const formData = new FormData();
    const dto = this.cleanDtoToSend(this.stationDialogData);

    if (this.fileState.logoPictureFile) {
      formData.append('logoPicture', this.fileState.logoPictureFile);
    }
    if (this.fileState.stationPictureFile) {
      formData.append('stationPicture', this.fileState.stationPictureFile);
    }
    if (this.fileState.outsidePictureFile) {
      formData.append('outsidePicture', this.fileState.outsidePictureFile);
    }

    // Append the DTO to FormData
    for (const key in dto) {
      if (dto[key] !== null && dto[key] !== undefined) {
        formData.append(key, dto[key]);
      }
    }

    // Append deleted images to FormData
    if (this.deletedPictures.length > 0) {
      formData.append('deletedPictures', JSON.stringify(this.deletedPictures));
    }

    formData.append('openingTimes', JSON.stringify(structuredClone(this.stationOpeningTimes).reduce((acc: any, element, index) => {
      if (element.enabled) {
        acc[index] = element.times;
      }
      return acc;
    }, {} as Record<number, { open: string; close: string }[]>)));

    this.http.post(this.ENV.apiUrl + (this.isNewStationDialog ? "createStation" : "editStation"), formData).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false
          })
        } else {
          this.refreshStations()
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: "stationSaved",
            addToHistory: false
          })
        }
        this.hideStationDialog()
        this.stationsLoading$.next(false);

      },
      error: (err) => {
        this.stationsLoading$.next(false);
      }
    })
  }

  deleteStation() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: "deleteStations",
      selection: [this.stationDialogData],
      message: "deleteStationConfirm",
      idKey: "stationId",
      onAcceptClick: () => {
        this.stationsLoading$.next(true);
      },
      successCallback: () => {
        this.refreshStations()
        this.hideStationDialog()
      },
      completeCallback: () => {
        this.stationsLoading$.next(false);
      }
    })
  }

  deleteStations() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: "deleteStations",
      selection: this.selectedStations,
      message: "deleteStationConfirm",
      idKey: "stationId",
      onAcceptClick: () => {
        this.stationsLoading$.next(true);
      },
      successCallback: () => {
        this.refreshStations()
        this.selectedStations = []
      },
      completeCallback: () => {
        this.stationsLoading$.next(false);
      }
    })
  }

  openBikeDialog(bike?: Bike) {
    if (bike) {
      this.bikesLoading$.next(true)
      this.http.post(this.ENV.apiUrl + "getBike", { ...bike, excludeRefs: ["currentStation", "status", "model"] }).subscribe({
        next: (resp: any) => {
          this.selectedBikes = []
          this.bikeDialogData = resp.data
          this.bikeDialogVisible = true;
          this.bikesLoading$.next(false);
          this.bikeDetailsSub?.unsubscribe()
          // if (bike.imei) {
          //   this.bikeDetailsSub = this.ws.subscribe(bike.imei).pipe(
          //     takeUntil(this.destroy$)
          //   ).subscribe({
          //     next: (raw) => {
          //       if(!raw || Object.keys(raw).length == 0 || !("data" in raw)) return
          //       let data = raw.data
          //       console.log(data);
          //     }
          //   })
          // }
        },
        error: () => {
          this.bikesLoading$.next(false);
        }
      })
    } else {
      if (!this.canCreateBike) return
      this.bikeDialogData = getDefaultBike()
      this.bikeDialogVisible = true
    }
  }

  hideBikeDialog() {
    this.bikeDialogVisible = false;
    this.bikeDialogData = getDefaultBike();
    this.setViewOnUser()
  }

  saveBike() {
    this.bikesLoading$.next(true);
    this.http.post(this.ENV.apiUrl + (this.isNewBikeDialog ? "createBike" : "editBike"), this.cleanDtoToSend(this.bikeDialogData)).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false
          })
        } else {
          let cnt = this.bikeDialogData.bikeId.length
          this.refreshBikes()
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: "bikeSaved",
            detailParams: {
              count: cnt
            },
            translateDetailsParams: false,
            addToHistory: false
          })
        }
        this.hideBikeDialog()
        this.bikesLoading$.next(false);
      },
      error: (err) => {
        this.bikesLoading$.next(false);
      }
    })
  }

  deleteBike() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: "deleteBikes",
      selection: [this.bikeDialogData],
      message: "deleteBikeConfirm",
      idKey: "bikeId",
      onAcceptClick: () => {
        this.bikesLoading$.next(true);
      },
      successCallback: () => {
        this.refreshBikes()
        this.hideBikeDialog()
      },
      completeCallback: () => {
        this.bikesLoading$.next(false);
      }
    })
  }

  deleteBikes() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: "deleteBikes",
      selection: this.selectedBikes,
      message: "deleteBikeConfirm",
      idKey: "bikeId",
      onAcceptClick: () => {
        this.bikesLoading$.next(true);
      },
      successCallback: () => {
        this.refreshBikes()
        this.selectedBikes = []
      },
      completeCallback: () => {
        this.bikesLoading$.next(false);
      }
    })
  }

  unlockBike(bike: Bike) {
    this.http.post(this.ENV.apiUrl + "unlockBikeForced", { imei: bike.imei })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: "bikeUnlocked",
            addToHistory: false
          })
        },
        error: (err) => {
        }
      })
  }

  rebootDevice(bike: Bike) {
    this.http.post(this.ENV.apiUrl + "rebootBikeForced", { imei: bike.imei })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: "deviceRebooted",
            addToHistory: false
          })
        },
        error: (err) => {
        }
      })
  }

  autocompleteImeis(e: AutoCompleteCompleteEvent) {
    this.iotConnectedImeisAutocomplete = this.iotConnectedImeis.filter(imei => imei.toLowerCase().includes(e.query.toLowerCase()))
  }

  synchingCoordinates = false;
  syncCoordinatesAndAddress() {
    this.synchingCoordinates = true;
    this.http.get("https://nominatim.openstreetmap.org/reverse?lat=" + this.stationDialogData.lat + "&lon=" + this.stationDialogData.lng + "&format=json&zoom=18&addressdetails=1").subscribe({
      next: (resp: any) => {
        this.synchingCoordinates = false;
        let addressData = resp.address;
        if (addressData) {
          this.stationDialogData.address = addressData.road ? addressData.road + (addressData.house_number ? " " + addressData.house_number : "") : undefined
          this.stationDialogData.city = addressData.hamlet ?? (addressData.village ?? (addressData.town ?? addressData.city))
          this.stationDialogData.province = addressData["ISO3166-2-lvl6"] ? addressData["ISO3166-2-lvl6"].split("-")?.[1] : addressData.county
          this.stationDialogData.zip = addressData.postcode
          this.stationDialogData.countryCode = addressData["ISO3166-2-lvl6"] ? addressData["ISO3166-2-lvl6"].split("-")?.[0] : (addressData.country_code ?? "").toUpperCase()
        }
      },
      error: () => {
        this.synchingCoordinates = false;
      }
    })
  }

  addressAutocomplete(evt: AutoCompleteCompleteEvent) {
    this.http.get("https://nominatim.openstreetmap.org/search?q=" + evt.query + "&format=json").subscribe({
      next: (resp: any) => {
        this.addressAutocompleteResults = resp.map((r: any) => {
          return {
            label: r.display_name,
            value: r.display_name,
            data: {
              lat: r.lat,
              lng: r.lon
            }
          }
        })
      }
    })
  }

  onAddressAutocompleteSelect(evt: AutoCompleteSelectEvent) {
    this.mapLocationPickerSelection = [parseFloat(evt.value.data.lat), parseFloat(evt.value.data.lng)];
    if (this.mapLocationPickerPin) {
      this.mapLocationPickerPin.remove();
    }
    this.mapLocationPickerPin = marker(this.mapLocationPickerSelection, {
      icon: divIcon({
        className: this.mapLocationPickerMode + 'marker-icon picker',
        html: '<div class="marker-circle"><span class="material-symbols-outlined ' + this.mapLocationPickerMode + '-icon">edit_location</span></div>',
        iconSize: [40, 40]
      })
    });
    this.mapLocationPickerPin.addTo(this.map!);
    this.panToPoint(evt.value.data.lat, evt.value.data.lng, 17)
  }

  openLocationPicker(mode: "station" | "bike") {
    this.mapLocationPickerMode = mode;

    let currentData: any = {}
    switch (mode) {
      case "station":
        this.stationDialogVisible = false;
        this.stationsReduced = true;
        this.bikesVisible = false;
        currentData = this.stationDialogData;
        break;
      case "bike":
        this.bikeDialogVisible = false;
        this.bikesReduced = true;
        this.stationsVisible = false;
        currentData = this.bikeDialogData;
        break;
    }
    this.updateMarkersState();
    this.hideStationFilters()

    if (this.mapLocationPickerPin) {
      this.mapLocationPickerPin.remove();
    }

    if (currentData.lat !== undefined && currentData.lng !== undefined && currentData.lat !== 0 && currentData.lng !== 0) {
      this.mapLocationPickerSelection = [parseFloat(currentData.lat.toString()), parseFloat(currentData.lng.toString())];
      this.mapLocationPickerPin = marker(this.mapLocationPickerSelection, {
        icon: divIcon({
          className: mode + '-marker-icon picker',
          html: '<div class="marker-circle"><span class="material-symbols-outlined ' + mode + '-icon">edit_location</span></div>',
          iconSize: [40, 40],
        })
      });
      this.mapLocationPickerPin.addTo(this.map!);
    }

    this.autocompleteSearchAddressQuery = ""
    this.addressAutocompleteResults = []

    this.mapLocationPickerActive = true;

    setTimeout(() => {
      this.map?.invalidateSize()
      if (currentData.lat !== undefined && currentData.lng !== undefined && currentData.lat !== 0 && currentData.lng !== 0) {
        this.panToPoint(currentData.lat, currentData.lng, 15)
      } else {
        this.setViewOnUser()
      }
    }, 1);
  }

  closeLocationPicker(latlng?: [number, number]) {

    let currentData: any = {}

    switch (this.mapLocationPickerMode) {
      case "station":
        this.stationsReduced = false;
        this.bikesVisible = true;
        this.bikesReduced = true;
        currentData = this.stationDialogData;
        this.stationDialogVisible = true;
        break;
      case "bike":
        this.bikesReduced = false;
        this.stationsVisible = true;
        this.stationsReduced = true;
        currentData = this.bikeDialogData;
        this.bikeDialogVisible = true;
        break;
    }

    if (latlng !== undefined) {
      currentData.lat = latlng[0];
      currentData.lng = latlng[1];
    }

    this.mapLocationPickerSelection = undefined;

    if (this.mapLocationPickerPin) {
      this.mapLocationPickerPin.remove();
      this.mapLocationPickerPin = undefined;
    }

    this.updateMarkersState();
    this.mapLocationPickerActive = false;
    setTimeout(() => {
      this.map?.invalidateSize()
    }, 1);
  }


  //? ALTRO
  translateObjectValues<T>(obj: T, keyToTranslate: string): T {
    if (Array.isArray(obj)) {
      return obj.map(item => this.translateObjectValues(item, keyToTranslate)) as T;
    }

    if (obj && typeof obj === 'object') {
      const result = { ...obj };
      for (const [key, value] of Object.entries(result)) {
        if (key === keyToTranslate && typeof value === 'string') {
          (result as { [key: string]: any })[key] = this.transloco.translate(value);
        } else if (typeof value === 'object') {
          (result as { [key: string]: any })[key] = this.translateObjectValues(value, keyToTranslate);
        }
      }
      return result as T;
    }
    return obj;
  }

  searchFilter(source: StationFilter, key: keyof StationFilter['options'], evt: AutoCompleteCompleteEvent) {
    source.cache[key] = source.options[key].filter((item: { label: string }) => item.label.toLowerCase().includes(evt.query.toLowerCase()));
  }

  onBikeFiltersDialogHide() {
    this.bikeFilters.values = structuredClone(this.bikeFilters.applied)
  }

  onStationFiltersDialogHide() {
    this.stationFilters.values = structuredClone(this.stationFilters.applied)
  }

  getCellData(col: Column, data: any) {
    let pipe = col.pipe

    let value = getPathKey(data, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>')

    if (pipe) {
      if (!Array.isArray(pipe)) {
        pipe = [pipe, {}]
      }
      let pipeconfig = pipe[1]
      pipeconfig = this.replaceObjectValues(pipeconfig, getPathKey(data, col.nestedField ?? col.field, col.defaultValue), data, value)
      switch (pipe[0]) {
        case "date":
          value = this.translocoDate.transform(value, pipeconfig, pipeconfig.locale);
          break

        case "currency":
          value = this.translocoCurrency.transform(value, pipeconfig.display, pipeconfig.numberFormatOptions, pipeconfig.currencyCode, pipeconfig.locale);
          break

        case "percent":
          value = this.translocoPercent.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'decimal':
          value = this.translocoDecimal.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'translate':
          value = this.transloco.translate(String(value), pipeconfig)
          break

        case 'duration':
          value = this.durationPipe.transform(value, pipeconfig);
          break

        case "custom":
          value = pipe[1](value)
          break

        default:
          return value;
      }
    }
    return value;
  }

  cleanDtoToSend(dto: any) {
    let res = structuredClone(dto)
    for (const key in res) {
      if (res[key] === undefined || res[key] === null || res[key] === "") {
        delete res[key]
      }
    }
    return res
  }

  parseWrapperInputs(col: Column, value: [any, any]) {
    let inputs = col.wrapperConfig;
    if (inputs) {
      inputs = structuredClone(inputs);
      const rowData = value[0];
      const currentColumnValue = value[1];
      const currentColumnValueOriginal = getPathKey(rowData, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>');
      return this.replaceObjectValues(inputs, currentColumnValue, rowData, currentColumnValueOriginal)
    } else {
      return {};
    }
  }

  replaceObjectValues(obj: { [key: string]: any }, currentColumnValue: any, rowData: any, currentColumnValueOriginal: any) {
    if (typeof obj === "object" && obj !== null) {
      let objectCopy = structuredClone(obj);

      for (let key in objectCopy) {
        if (typeof objectCopy[key] === 'string') {
          // Replace {{columnField}} with the corresponding value from rowData
          objectCopy[key] = objectCopy[key].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
            if (path === 'value') {
              return currentColumnValue;
            }
            return getPathKey(rowData, path, match);
          });
        } else if (typeof objectCopy[key] === 'object') {
          // iterate thru key-val
          for (let subKey in objectCopy[key]) {
            if (typeof objectCopy[key][subKey] === 'string') {
              // Replace {{columnField}} with the corresponding value from rowData
              objectCopy[key][subKey] = objectCopy[key][subKey].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
                let res = currentColumnValue
                if (path !== 'value') {
                  res = getPathKey(rowData, path, match);
                }
                if (subKey == "--state-color" && res == "base") {
                  res = "var(--p-surface-100)"
                }
                if (subKey == "--state-color-text") {
                  res = res == "base" ? "var(--p-text-color)" : "var(--p-primary-color-text)"
                }
                return res
              });
            }
          }
        }

        const match = key.match(/^(.+)_if_(.+)_(.+)$/);
        if (match) {
          const baseKey = match[1];
          const compareType = match[2];
          const compareValue = match[3];

          let valueToCompare;
          if (compareType === 'val') {
            valueToCompare = currentColumnValueOriginal;
          } else {
            valueToCompare = getPathKey(rowData, compareType, undefined);
          }

          if (String(valueToCompare) === compareValue) {
            if (typeof objectCopy[key] == "string") {
              objectCopy[baseKey] = objectCopy[key].startsWith("%+t%") ?
                this.transloco.translate(objectCopy[key].replace("%+t%", "")) :
                objectCopy[key].replace("%+t%", "");
            } else {
              objectCopy[baseKey] = objectCopy[key]
            }

          }
          delete objectCopy[key];
        }
      }
      return objectCopy;
    }
    return obj
  }


  onFileSelected(event: any, field: string) {
    const file = event.target.files[0];
    if (file) {
      // Store the file as a binary object
      (this.fileState as any)[`${field}File`] = file;

      // Generate a preview for the UI
      const reader = new FileReader();
      reader.onload = () => {
        (this.fileState as any)[`${field}Preview`] = reader.result as string;
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      }
      reader.readAsDataURL(file);
    }
  }

  clearImage(field: string) {
    // If there's an existing image (from the backend), add it to deletedPictures
    if ((this.fileState as any)[`${field}Preview`] && !(this.fileState as any)[`${field}File`]) {
      this.deletedPictures.push(field);
    }

    // Clear the preview and file
    (this.fileState as any)[`${field}Preview`] = undefined;
    (this.fileState as any)[`${field}File`] = undefined;
  }

  getDayIndices(): number[] {
    const locale = this.localeService.getLocale()

    if (['en-US'].includes(locale)) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    return [1, 2, 3, 4, 5, 6, 0];
  }
}
